/**
 * Guide Snapping
 *
 * Calculates snapped drag offset from alignment and spacing guides.
 * Pure function — no side effects.
 */

import type { Point, AlignmentGuide, SpacingGuide, GuideSnapResult, ResizeGuideSnapResult, NodeEdges, ResizeHandlePosition } from './types';

/** Default snap threshold in screen pixels */
export const GUIDE_SNAP_THRESHOLD_PX = 6;

/**
 * Snap a drag offset to the closest alignment/spacing guides.
 *
 * Per axis: picks the closest guide within threshold and adjusts the offset.
 * If altHeld, returns guides for rendering but doesn't adjust the offset.
 */
export function snapToGuides(
	rawOffset: Point,
	draggedBounds: NodeEdges,
	allGuides: AlignmentGuide[],
	allSpacingGuides: SpacingGuide[],
	altHeld: boolean,
): GuideSnapResult {
	let snapX: { delta: number; guides: AlignmentGuide[] } | null = null;
	let snapY: { delta: number; guides: AlignmentGuide[] } | null = null;

	// Find closest guide per axis from alignment guides
	for (const guide of allGuides) {
		const edgeValue = getEdgeValue(draggedBounds, guide.sourceEdge);
		const delta = guide.position - edgeValue;
		const absDelta = Math.abs(delta);

		if (guide.axis === 'x') {
			if (!snapX || absDelta < Math.abs(snapX.delta)) {
				snapX = { delta, guides: [guide] };
			} else if (absDelta === Math.abs(snapX.delta) && delta === snapX.delta) {
				snapX.guides.push(guide);
			}
		} else {
			if (!snapY || absDelta < Math.abs(snapY.delta)) {
				snapY = { delta, guides: [guide] };
			} else if (absDelta === Math.abs(snapY.delta) && delta === snapY.delta) {
				snapY.guides.push(guide);
			}
		}
	}

	// Collect active spacing guides (already within threshold from detection)
	const activeSpacingGuides = allSpacingGuides;

	// Build snapped offset
	const snappedAxes = { x: false, y: false };
	let offset = { ...rawOffset };

	if (!altHeld) {
		if (snapX) {
			offset = { ...offset, x: rawOffset.x + snapX.delta };
			snappedAxes.x = true;
		}
		if (snapY) {
			offset = { ...offset, y: rawOffset.y + snapY.delta };
			snappedAxes.y = true;
		}
	}

	// Collect all active guides (for rendering)
	const activeGuides: AlignmentGuide[] = [
		...(snapX?.guides ?? []),
		...(snapY?.guides ?? []),
	];

	return {
		offset,
		guides: activeGuides,
		spacingGuides: activeSpacingGuides,
		snappedAxes,
	};
}

/** Get the world coordinate of a named edge from node bounds. */
function getEdgeValue(bounds: NodeEdges, edge: string): number {
	switch (edge) {
		case 'top': return bounds.top;
		case 'bottom': return bounds.bottom;
		case 'left': return bounds.left;
		case 'right': return bounds.right;
		case 'centerX': return bounds.centerX;
		case 'centerY': return bounds.centerY;
		default: return 0;
	}
}

// =============================================================================
// Resize Guide Snapping
// =============================================================================

/** Free edges per handle — only these can snap during resize. */
const FREE_EDGES: Record<ResizeHandlePosition, string[]> = {
	se: ['right', 'bottom', 'centerX', 'centerY'],
	nw: ['left', 'top', 'centerX', 'centerY'],
	ne: ['right', 'top', 'centerX', 'centerY'],
	sw: ['left', 'bottom', 'centerX', 'centerY'],
	e:  ['right', 'centerX'],
	w:  ['left', 'centerX'],
	n:  ['top', 'centerY'],
	s:  ['bottom', 'centerY'],
};

/**
 * Snap resize bounds to alignment guides, keeping the anchor edge fixed.
 *
 * For each free edge on the current handle, finds the closest matching guide
 * and adjusts the bounds so that edge aligns while the opposite edge stays put.
 * Edge snaps are prioritized over center snaps at similar distances.
 *
 * If altHeld, returns guides for rendering but doesn't adjust bounds.
 */
export function snapResizeToGuides(
	currentBounds: { cx: number; cy: number; width: number; height: number },
	handle: ResizeHandlePosition,
	allGuides: AlignmentGuide[],
	altHeld: boolean,
	minSize: { width: number; height: number } = { width: 50, height: 50 },
): ResizeGuideSnapResult {
	const freeEdges = new Set(FREE_EDGES[handle]);

	// Filter to only guides whose sourceEdge is free for this handle
	const relevantGuides = allGuides.filter((g) => freeEdges.has(g.sourceEdge));

	// Find closest guide per axis, preferring edge over center at similar distance
	let snapX: { guide: AlignmentGuide; delta: number } | null = null;
	let snapY: { guide: AlignmentGuide; delta: number } | null = null;

	const bounds = boundsToEdges(currentBounds);

	for (const guide of relevantGuides) {
		const edgeValue = getEdgeValue(bounds, guide.sourceEdge);
		const delta = guide.position - edgeValue;
		const absDelta = Math.abs(delta);

		if (guide.axis === 'x') {
			if (!snapX || absDelta < Math.abs(snapX.delta) ||
				(absDelta === Math.abs(snapX.delta) && guide.type === 'edge' && snapX.guide.type === 'center')) {
				snapX = { guide, delta };
			}
		} else {
			if (!snapY || absDelta < Math.abs(snapY.delta) ||
				(absDelta === Math.abs(snapY.delta) && guide.type === 'edge' && snapY.guide.type === 'center')) {
				snapY = { guide, delta };
			}
		}
	}

	// Collect guides for rendering (even when alt held)
	const activeGuides: AlignmentGuide[] = [];
	if (snapX) activeGuides.push(snapX.guide);
	if (snapY) activeGuides.push(snapY.guide);

	const snappedAxes = { x: false, y: false };
	let result = { ...currentBounds };

	if (!altHeld) {
		if (snapX) {
			result = applyEdgeSnap(result, handle, 'x', snapX.guide.sourceEdge, snapX.guide.position, minSize);
			snappedAxes.x = true;
		}
		if (snapY) {
			result = applyEdgeSnap(result, handle, 'y', snapY.guide.sourceEdge, snapY.guide.position, minSize);
			snappedAxes.y = true;
		}
	}

	return { bounds: result, guides: activeGuides, spacingGuides: [], snappedAxes };
}

/** Convert center-based bounds to NodeEdges. */
function boundsToEdges(b: { cx: number; cy: number; width: number; height: number }): NodeEdges {
	return {
		left: b.cx - b.width / 2,
		right: b.cx + b.width / 2,
		top: b.cy - b.height / 2,
		bottom: b.cy + b.height / 2,
		centerX: b.cx,
		centerY: b.cy,
	};
}

/**
 * Adjust bounds so that `sourceEdge` lands at `targetPos`, keeping the anchor fixed.
 * The anchor is the opposite side from the handle's free edge.
 */
function applyEdgeSnap(
	bounds: { cx: number; cy: number; width: number; height: number },
	handle: ResizeHandlePosition,
	_axis: 'x' | 'y',
	sourceEdge: string,
	targetPos: number,
	minSize: { width: number; height: number },
): { cx: number; cy: number; width: number; height: number } {
	const edges = boundsToEdges(bounds);
	let { cx, cy, width, height } = bounds;

	if (sourceEdge === 'right') {
		const fixedLeft = edges.left;
		width = Math.max(targetPos - fixedLeft, minSize.width);
		cx = fixedLeft + width / 2;
	} else if (sourceEdge === 'left') {
		const fixedRight = edges.right;
		width = Math.max(fixedRight - targetPos, minSize.width);
		cx = fixedRight - width / 2;
	} else if (sourceEdge === 'bottom') {
		const fixedTop = edges.top;
		height = Math.max(targetPos - fixedTop, minSize.height);
		cy = fixedTop + height / 2;
	} else if (sourceEdge === 'top') {
		const fixedBottom = edges.bottom;
		height = Math.max(fixedBottom - targetPos, minSize.height);
		cy = fixedBottom - height / 2;
	} else if (sourceEdge === 'centerX') {
		// Center snap: fixed point depends on handle
		const fixedX = getFixedX(handle, edges);
		width = Math.max(2 * Math.abs(targetPos - fixedX), minSize.width);
		cx = fixedX + (targetPos > fixedX ? width / 2 : -width / 2);
	} else if (sourceEdge === 'centerY') {
		const fixedY = getFixedY(handle, edges);
		height = Math.max(2 * Math.abs(targetPos - fixedY), minSize.height);
		cy = fixedY + (targetPos > fixedY ? height / 2 : -height / 2);
	}

	return { cx, cy, width, height };
}

function getFixedX(handle: ResizeHandlePosition, edges: NodeEdges): number {
	if (handle === 'nw' || handle === 'w' || handle === 'sw') return edges.right;
	return edges.left; // se, e, ne
}

function getFixedY(handle: ResizeHandlePosition, edges: NodeEdges): number {
	if (handle === 'nw' || handle === 'n' || handle === 'ne') return edges.bottom;
	return edges.top; // sw, s, se
}
